import { Link } from '@tanstack/react-router'
import {
  ClipboardCheck,
  GitBranch,
  ListChecks,
  MessageSquareText,
  Rocket,
  Route,
  Settings,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const cockpitItems = [
  {
    label: 'Overview',
    icon: ClipboardCheck,
    to: '/projects/$workspaceUuid/$projectUuid',
    tab: 'overview',
  },
  {
    label: 'Timeline',
    icon: Route,
    to: '/projects/$workspaceUuid/$projectUuid/provisioning',
    tab: 'timeline',
  },
  {
    label: 'Kanban',
    icon: GitBranch,
    tab: 'kanban',
  },
  {
    label: 'Settings',
    icon: Settings,
    tab: 'settings',
  },
  {
    label: 'PM Chat',
    icon: MessageSquareText,
    tab: 'pm-chat',
  },
  {
    label: 'Action Items',
    icon: ListChecks,
    tab: 'action-items',
  },
  {
    label: 'Releases',
    icon: Rocket,
    tab: 'releases',
  },
] as const

export type ProjectCockpitTab = (typeof cockpitItems)[number]['tab']

export function ProjectCockpitNav({
  workspaceUuid,
  projectUuid,
  active,
  disabledItems = {},
}: {
  workspaceUuid: string
  projectUuid: string
  active: ProjectCockpitTab
  disabledItems?: Partial<Record<(typeof cockpitItems)[number]['label'], string>>
}) {
  return (
    <nav
      aria-label="Project cockpit"
      className="flex gap-2 overflow-x-auto rounded-lg border border-white/10 bg-white/[0.03] p-1"
    >
      {cockpitItems.map((item) => {
        const Icon = item.icon
        const disabledReason = disabledItems[item.label]
        const isActive = active === item.tab
        if (disabledReason) {
          return (
            <span
              key={item.label}
              title={disabledReason}
              aria-disabled="true"
              className="inline-flex min-h-9 shrink-0 cursor-not-allowed items-center gap-2 rounded-md px-3 text-sm font-medium text-muted-foreground/60"
            >
              <Icon className="size-4" />
              {item.label}
            </span>
          )
        }
        return (
          <Link
            key={item.label}
            to={'to' in item ? item.to : '/projects/$workspaceUuid/$projectUuid'}
            params={{ workspaceUuid, projectUuid }}
            search={item.tab === 'overview' || 'to' in item ? undefined : { tab: item.tab }}
            className={cn(
              'inline-flex min-h-9 shrink-0 items-center gap-2 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors',
              'hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
              isActive && 'bg-white/[0.08] text-foreground shadow-soft',
            )}
          >
            <Icon className="size-4" />
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}
